/**
 * Create User Modal
 *
 * Modal component for creating new users with comprehensive form validation.
 * Includes fields for personal information, role assignment, and system settings.
 *
 * Features:
 * - Full form validation
 * - Role selection dropdown
 * - Department selection
 * - Locale and timezone configuration
 * - Real-time validation feedback
 * - React Query mutation with optimistic updates
 *
 * @component
 */

import React, { useMemo, useState } from 'react';
import { X, UserPlus, AlertCircle } from 'lucide-react';
import {
  useCreateUser,
  getDepartments,
  type CreateUserPayload
} from '../../../services/queries/userQueries';
import { useRoles } from '../../../services/queries/roleQueries';
import { useToast } from '../../../contexts/ToastContext';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { StandardButton, StandardInput, StandardSelect } from '../../standard';
import { RolePermissionPreview } from './RolePermissionPreview';

// ============================================
// INTERFACES
// ============================================

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string; // Initial/temporary password; if empty, backend uses default
  phone: string;
  title: string;
  department: string;
  roleIds: string[]; // Changed to array for multi-select
  locale: string;
  timezone: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  password?: string;
  phone?: string;
  roleIds?: string;
}

// ============================================
// MAIN COMPONENT
// ============================================

export const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose }) => {
  const createUser = useCreateUser();
  const { success, error } = useToast();
  const { logChange } = useSetupChanges();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    phone: '',
    title: '',
    department: '',
    roleIds: [],
    locale: 'en_US',
    timezone: 'America/New_York'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get options
  const departments = getDepartments();
  // Fetch roles using React Query hook (supports both Gobodo API and mock data)
  const { data: roles = [], isLoading: rolesLoading } = useRoles({ includeSystem: true });
  const selectedRoles = useMemo(
    () => roles.filter((role) => formData.roleIds.includes(role.id)),
    [roles, formData.roleIds]
  );

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9_]{3,20}$/.test(formData.username)) {
      newErrors.username = 'Username must be 3-20 characters (lowercase letters, numbers, underscores)';
    }
    // Role validation - at least one role should be selected
    if (!formData.roleIds || formData.roleIds.length === 0) {
      newErrors.roleIds = 'At least one role is required';
    }

    // Phone validation (optional, but validate if provided)
    if (formData.phone && !/^\+?[\d\s()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleChange = (field: keyof FormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRoleToggle = (roleId: string) => {
    const currentRoleIds = formData.roleIds || [];
    const newRoleIds = currentRoleIds.includes(roleId)
      ? currentRoleIds.filter(id => id !== roleId)
      : [...currentRoleIds, roleId];
    handleChange('roleIds', newRoleIds);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Prepare payload (password: use form value or backend will use REACT_APP_DEFAULT_USER_PASSWORD)
    const payload: CreateUserPayload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      username: formData.username.trim().toLowerCase(),
      password: formData.password.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      title: formData.title.trim() || undefined,
      department: formData.department || undefined,
      roleIds: formData.roleIds.length > 0 ? formData.roleIds : undefined,
      locale: formData.locale,
      timezone: formData.timezone
    };

    createUser.mutate(payload, {
      onSuccess: () => {
        const userName = `${formData.firstName} ${formData.lastName}`;

        // Track user creation
        logChange({
          action: 'created',
          entityType: 'user',
          entityName: userName,
          description: `created user ${userName}`,
          performedBy: 'Admin'
        });

        success('User created successfully!');
        onClose();
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          username: '',
          password: '',
          phone: '',
          title: '',
          department: '',
          roleIds: [],
          locale: 'en_US',
          timezone: 'America/New_York'
        });
      },
      onError: (err: any) => {
        console.error('Failed to create user:', err);
        error('Failed to create user', err.message || 'Please try again');
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    });
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
              <UserPlus className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Create New User
            </h2>
          </div>
          <StandardButton
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </StandardButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="John"
                  error={errors.firstName}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Doe"
                  error={errors.lastName}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.lastName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john.doe@example.com"
                  error={errors.email}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <StandardInput
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="jdoe"
                  error={errors.username}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Initial password (optional; backend uses default if empty) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Initial password
                </label>
                <StandardInput
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Leave blank to use default"
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Leave blank to use the configured default password for new accounts.
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <StandardInput
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  error={errors.phone}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <StandardInput
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Software Engineer"
                />
              </div>
            </div>
          </div>

          {/* Role and Department */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Role & Department
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Roles (Multi-select) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Roles <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                  {rolesLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading roles...</p>
                  ) : roles.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No roles available</p>
                  ) : (
                    <div className="space-y-2">
                      {roles.map(role => (
                        <label
                          key={role.id}
                          className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.roleIds.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                            {role.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.roleIds && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.roleIds}
                  </p>
                )}
                {formData.roleIds.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {formData.roleIds.length} role{formData.roleIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                <div className="mt-3">
                  <RolePermissionPreview roles={selectedRoles} title="Selected Role Preview" />
                </div>
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <StandardSelect
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  options={[
                    { value: '', label: 'Select a department...' },
                    ...departments.map(dept => ({ value: dept, label: dept }))
                  ]}
                />
              </div>
            </div>
          </div>

          {/* System Settings */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              System Settings
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* Locale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Locale
                </label>
                <StandardSelect
                  value={formData.locale}
                  onChange={(e) => handleChange('locale', e.target.value)}
                  options={[
                    { value: 'en_US', label: 'English (US)' },
                    { value: 'en_GB', label: 'English (UK)' },
                    { value: 'es_ES', label: 'Spanish' },
                    { value: 'fr_FR', label: 'French' }
                  ]}
                />
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timezone
                </label>
                <StandardSelect
                  value={formData.timezone}
                  onChange={(e) => handleChange('timezone', e.target.value)}
                  options={[
                    { value: 'America/New_York', label: 'Eastern Time' },
                    { value: 'America/Chicago', label: 'Central Time' },
                    { value: 'America/Denver', label: 'Mountain Time' },
                    { value: 'America/Los_Angeles', label: 'Pacific Time' },
                    { value: 'Europe/London', label: 'London' },
                    { value: 'Europe/Paris', label: 'Paris' }
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <StandardButton
              variant="secondary"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              leftIcon={isSubmitting ? undefined : <UserPlus className="h-4 w-4" />}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating...
                </span>
              ) : (
                'Create User'
              )}
            </StandardButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
